import { useState, useCallback, useMemo } from "react";

/**
 * Custom hook for filtering graph data arrays by date range indices.
 *
 * @param {Object} params
 * @param {string[]} params.weeks - Original weeks array
 * @param {Object}   params.dataArrays - Object mapping keys → original data arrays
 *                     e.g. { visitors: [...], percentOfVisitors: [...] }
 *
 * @returns {Object}  { filteredWeeks, filteredData, onFilterChange }
 */
const useDateRangeFilter = ({ weeks = [], dataArrays = {} }) => {
  const [filteredIndices, setFilteredIndices] = useState(null);

  const onFilterChange = useCallback(({ filteredIndices: indices }) => {
    setFilteredIndices(indices);
  }, []);

  const filteredWeeks = useMemo(() => {
    if (!filteredIndices) return weeks;
    return filteredIndices.map((i) => weeks[i]).filter(Boolean);
  }, [weeks, filteredIndices]);

  const filteredData = useMemo(() => {
    if (!filteredIndices) return dataArrays;

    const result = {};
    for (const [key, arr] of Object.entries(dataArrays)) {
      if (Array.isArray(arr)) {
        result[key] = filteredIndices.map((i) => arr[i]).filter((v) => v !== undefined);
      } else {
        result[key] = arr;
      }
    }
    return result;
  }, [dataArrays, filteredIndices]);

  return { filteredWeeks, filteredData, onFilterChange };
};

export default useDateRangeFilter;
